import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/services/api';
import { authService } from '@/services/auth';
import { toast } from 'sonner';

const STUDIO_URL = import.meta.env.VITE_STUDIO_URL || 'http://localhost:5174';

interface VisualBuilderEmbedProps {
  projectId: string;
}

export default function VisualBuilderEmbed({ projectId }: VisualBuilderEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [studioReady, setStudioReady] = useState(false);

  const deployMutation = useMutation({
    mutationFn: () => apiClient.deployApplication(projectId),
    onSuccess: () => toast.success('Deployment started'),
    onError: (error: Error) => toast.error(error.message || 'Failed to start deployment'),
  });

  const sendAuthToStudio = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    const token = authService.getToken();
    if (!token) return;

    iframeRef.current.contentWindow.postMessage(
      {
        type: 'zewst:auth',
        token,
        apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
      },
      STUDIO_URL
    );
  }, []);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== new URL(STUDIO_URL).origin) return;

      const { type, ...data } = event.data || {};
      switch (type) {
        case 'zewst:studio-ready':
          setStudioReady(true);
          sendAuthToStudio();
          break;
        case 'zewst:request-auth':
          sendAuthToStudio();
          break;
        case 'zewst:deploy':
          deployMutation.mutate();
          break;
        case 'zewst:toast':
          if (data.variant === 'error') toast.error(data.message);
          else toast.success(data.message);
          break;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendAuthToStudio, deployMutation]);

  useEffect(() => {
    if (studioReady) sendAuthToStudio();
  }, [studioReady, sendAuthToStudio]);

  const studioSrc = `${STUDIO_URL}/project/${projectId}`;

  return (
    <div className="h-full relative">
      {!studioReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading studio...</p>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={studioSrc}
        className="w-full h-full border-0"
        title="Zewst Studio"
        allow="clipboard-read; clipboard-write"
        onLoad={() => {
          setTimeout(() => {
            if (!studioReady) {
              setStudioReady(true);
              sendAuthToStudio();
            }
          }, 2000);
        }}
      />
    </div>
  );
}
